import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  VStack,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spinner,
  Center,
  Input,
  Select,
} from "@chakra-ui/react";
import { FiArrowLeft, FiDownload, FiImage } from "react-icons/fi";
import Card from "components/card/Card";
import { ChevronDownIcon, EditIcon, CheckIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminInvoiceDetails, updateInvoiceStatus, updateInvoiceTerms } from 'features/invoices/actions';
import PdfIcon from 'assets/img/pdf-icon.svg';
import StatusUpdateModal from 'components/modals/StatusUpdateModal';
import { useToastNotification } from 'utils/toast-utils';
import { fetchTerms } from 'features/terms/actions';
import DocumentViewerModal from 'components/modals/DocumentViewerModal';

const statusOptions = [
  "Pending",
  "Draft",
  "Under Review",
  "Approved",
  "Rejected",
  "Funded",
  "Incomplete",
  "Closed",
  "Deleted"
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Add this new component for rendering rejection reasons
const RejectionReasonsTab = ({ rejections }) => {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  
  return (
    <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
      <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={6}>
        Rejection History
      </Text>
      <VStack spacing={4} align="stretch">
        {rejections.map((rejection) => (
          <Flex
            key={rejection._id}
            p={4}
            borderWidth="1px"
            borderColor="red.100"
            borderRadius="md"
            bg="red.50"
            position="relative"
          >
            <Box
              position="absolute"
              left="-1px"
              top="0"
              bottom="0"
              width="4px"
              bg="red.500"
              borderTopLeftRadius="md"
              borderBottomLeftRadius="md"
            />
            <VStack align="stretch" spacing={2} flex="1">
              <Text color="red.700" fontWeight="500">
                {rejection.reason}
              </Text>
              <HStack spacing={4} color="gray.500" fontSize="sm">
                <Text>
                  Rejected on {new Date(rejection.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </Text>
                <Text>
                  at {new Date(rejection.createdAt).toLocaleTimeString()}
                </Text>
              </HStack>
            </VStack>
          </Flex>
        ))}
      </VStack>
    </Card>
  );
};

const InvoiceDetails = ({ invoiceData, onBack, onStatusChange }) => {
  console.log("🚀 ~ InvoiceDetails ~ invoiceData:", invoiceData)
  const dispatch = useDispatch();
  const { selectedInvoice, loading, error } = useSelector((state) => state.invoices);
  console.log("🚀 ~ InvoiceDetails ~ selectedInvoice:", selectedInvoice)
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgInput = useColorModeValue("transparent", "navy.900");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const menuBg = useColorModeValue("gray.100", "gray.700");
  const menuHoverBg = useColorModeValue("gray.200", "gray.600");
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const { showToast } = useToastNotification();
  const [editingSection, setEditingSection] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [availableTerms, setAvailableTerms] = useState([]);
  const [loadingSave, setLoadingSave] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    if (invoiceData?._id) {
      dispatch(fetchAdminInvoiceDetails(invoiceData._id));
    }
  }, [dispatch, invoiceData?._id]);

  useEffect(() => {
    dispatch(fetchTerms({ page: 1, limit: 0 }))
      .unwrap()
      .then(response => {
        setAvailableTerms(response.terms);
      })
      .catch(error => {
        console.error('Error fetching terms:', error);
      });
  }, [dispatch]);

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="200px">
        <Text color="red.500">Error: {error}</Text>
      </Center>
    );
  }

  // Use selectedInvoice instead of invoiceData prop
  const invoice = selectedInvoice || invoiceData;

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.500';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return 'orange.500';
      case 'UNDER REVIEW':
        return 'purple.500';
      case 'APPROVED':
        return 'green.500';
      case 'REJECTED':
        return 'red.500';
      case 'FUNDED':
        return 'teal.500';
      case 'INCOMPLETE':
        return 'yellow.500';
      case 'DELETED':
        return 'gray.500';
      case 'CLOSED':
        return 'blue.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusBgColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.100';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return 'orange.100';
      case 'UNDER REVIEW':
        return 'purple.100';
      case 'APPROVED':
        return 'green.100';
      case 'REJECTED':
        return 'red.100';
      case 'FUNDED':
        return 'teal.100';
      case 'INCOMPLETE':
        return 'yellow.100';
      case 'DELETED':
        return 'gray.100';
      case 'CLOSED':
        return 'blue.100';
      default:
        return 'gray.100';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'Approved';
      case 'UNDER REVIEW':
        return 'Under Review';
      case 'REJECTED':
        return 'Rejected';
      case 'FUNDED':
        return 'Funded';
      case 'INCOMPLETE':
        return 'Incomplete';
      case 'CLOSED':
        return 'Closed';
      case 'DELETED':
        return 'Deleted';
      case 'PENDING':
        return 'Pending';
      case 'DRAFT':
        return 'Draft';
      default:
        return status || 'N/A';
    }
  };

  const handleStatusChange = (status) => {
    const remainingAmount = parseFloat(invoice?.remainingAmount) || 0;
    
    // Prevent status change to "Closed" if there's remaining balance
    if (status === "Closed" && remainingAmount > 0) {
      showToast(
        'Error',
        'Cannot close invoice with remaining balance',
        'error'
      );
      return;
    }

    // Prevent status change to "Funded" if termId is not present
    if (status === "Funded" && !invoice?.termId) {
      showToast(
        'Error',
        'Cannot fund invoice without terms',
        'error'
      );
      return;
    }

    const systemStatus = status === "Delete Invoice" 
      ? "Deleted" 
      : status;
    
    setSelectedStatus(systemStatus);
    setIsStatusModalOpen(true);
  };

  const handleStatusUpdateConfirm = async (status, reason = '') => {
    try {
      setLoadingStatus(true);
      await dispatch(updateInvoiceStatus({
        invoiceId: invoice._id,
        status,
        reason
      })).unwrap();
      
      showToast('Success', 'Invoice status updated successfully', 'success');
      setIsStatusModalOpen(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update status', 'error');
    } finally {
      setLoadingStatus(false);
    }
  };

  const getFileName = (url) => {
    if (!url) return 'Unknown';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const handleEditClick = (section) => {
    setEditingSection(section);
    if (section === 'information') {
      setEditedData({
        invoiceNumber: invoice?.invoiceNumber || '',
        client: invoice?.userId?.companyName || '',
        reference: invoice?.referenceNumber || '',
        invoiceDate: invoice?.date || '',
        debtor: invoice?.debtorName || '',
        latePaymentFees: invoice?.latePaymentFees || '',
      });
    } else if (section === 'amount') {
      setEditedData({
        invoiceAmount: invoice?.amount || '',
        advanceAmount: invoice?.advanceAmount || '',
        escrowAmount: invoice?.escrowAmount || '',
        feeAmount: invoice?.feeAmount || '',
        latePaymentFees: invoice?.latePaymentFees || '',
      });
    } else if (section === 'terms') {
      setEditedData({
        termId: invoice?.termId || '',
        escrowReserveRate: invoice?.escrowReserveRate || '',
        purchaseRate: invoice?.purchaseRate || '',
        advanceRate: invoice?.advanceRate || '',
        feePerInvoice: invoice?.feePerInvoice || '',
        minimumPurchaseFee: invoice?.minimumPurchaseFee || '',
      });
    }
  };

  const handleSave = async (section) => {
    try {
      setLoadingSave(true);
      if (section === 'terms') {
        await dispatch(updateInvoiceTerms({
          invoiceId: invoice._id,
          termId: editedData.termId
        })).unwrap();
      }
      // Add other section updates here when needed
      
      showToast('Success', 'Changes saved successfully', 'success');
      setEditingSection(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to save changes', 'error');
    } finally {
      setLoadingSave(false);
    }
  };

  const handleClose = () => {
    setEditingSection(null);
    setEditedData({});
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderSaveButton = (section) => (
    <Button 
      colorScheme="purple" 
      onClick={() => handleSave(section)}
      isLoading={loadingSave}
      loadingText="Saving..."
      isDisabled={loadingSave}
    >
      Save
    </Button>
  );

  // Update the isActionsDisabled function to check for remaining balance
  const isActionsDisabled = () => {
    const remainingAmount = parseFloat(invoice?.remainingAmount) || 0;
    return invoice?.status === 'CLOSED' && remainingAmount > 0;
  };

  // Update the renderInformationSection to disable edit button
  const renderInformationSection = () => (
    <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A" mb={6} position="relative">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="1rem" fontWeight="600" color="#42505C">
          Information
        </Text>
      </Flex>
      {editingSection === 'information' ? (
        <>
          <Flex wrap="wrap" gap={8}>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Invoice Number</Text>
              <Input
                value={editedData.invoiceNumber}
                onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                placeholder="Invoice Number"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Client</Text>
              <Input
                value={editedData.client}
                onChange={(e) => handleInputChange('client', e.target.value)}
                placeholder="Client"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Reference</Text>
              <Input
                value={editedData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                placeholder="Reference"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Invoice Date</Text>
              <Input
                value={editedData.invoiceDate}
                onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                placeholder="Invoice Date"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Debtor</Text>
              <Input
                value={editedData.debtor}
                onChange={(e) => handleInputChange('debtor', e.target.value)}
                placeholder="Debtor"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Late Payment Fees</Text>
              <Input
                value={editedData.latePaymentFees}
                onChange={(e) => handleInputChange('latePaymentFees', e.target.value)}
                placeholder="Late Payment Fees"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Funded Date</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.fundDate)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Created On</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.createdAt) || '-'}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Days Outstanding</Text>
              <Text color={textColor} fontWeight="500">
                {(() => {
                  if (!invoice?.expectedDueDate || !invoice?.date || invoice?.status === 'Closed') return '-';
                  console.log( invoice.status);
                  const dueDate = new Date(invoice.expectedDueDate);
                  const invoiceDate = new Date();
                  const diffTime = dueDate - invoiceDate;
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays;
                })()}
              </Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Status</Text>
              <Text color={textColor} fontWeight="500">{getStatusDisplayName(invoice?.status)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Payment Status</Text>
              <Text color={textColor} fontWeight="500">
                {(() => {
                  const amount = parseFloat(invoice?.amount) || 0;
                  const remainingAmount = parseFloat(invoice?.remainingAmount) || 0;
                  
                  if (remainingAmount === amount) return 'Pending';
                  if (remainingAmount === 0) return 'Fully Paid';
                  if (remainingAmount < amount) return 'Partially Paid';
                  return 'Unknown';
                })()}
              </Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Payment Amount</Text>
              <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.paymentAmount || 0)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Paid On</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.paidOn) || '-'}</Text>
            </Box>
          </Flex>
          <Flex justify="flex-end" mt={4} gap={2}>
            {renderSaveButton('information')}
            <Button variant="ghost" onClick={handleClose} isDisabled={loadingSave}>Close</Button>
          </Flex>
        </>
      ) : (
        <Flex direction="column" gap={8}>
          <Flex wrap="wrap" gap={8}>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Invoice Number</Text>
              <Text color={textColor} fontWeight="500">{invoice?.referenceNumber || '-'}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Client</Text>
              <Text color={textColor} fontWeight="500">{invoice?.userId?.companyName || '-'}</Text>
            </Box>
            {/* <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Reference here</Text>
              <Text color={textColor} fontWeight="500">{invoice?.referenceNumber || '-'}</Text>
            </Box> */}
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Invoice Date</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.date) || '-'}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Debtor</Text>
              <Text color={textColor} fontWeight="500">{invoice?.debtorName}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Funded Date</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.fundDate)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Created On</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.createdAt) || '-'}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Late Payment Fees</Text>
              <Text color={textColor} fontWeight="500">{invoice?.latePaymentFees || '0.15%'}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Days Outstanding</Text>
              <Text color={textColor} fontWeight="500">
                {(() => {
                  if (!invoice?.expectedDueDate || !invoice?.date || invoice?.status === 'Closed') return '-';
                  const dueDate = new Date(invoice.expectedDueDate);
                  const invoiceDate = new Date();
                  const diffTime = dueDate - invoiceDate;
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays;
                })()}
              </Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Status</Text>
              <Text color={textColor} fontWeight="500">{getStatusDisplayName(invoice?.status)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Payment Status</Text>
              <Text color={textColor} fontWeight="500">
                {(() => {
                  const amount = parseFloat(invoice?.amount) || 0;
                  const remainingAmount = parseFloat(invoice?.remainingAmount) || 0;
                  
                  if (remainingAmount === amount) return 'Pending';
                  if (remainingAmount === 0) return 'Fully Paid';
                  if (remainingAmount < amount) return 'Partially Paid';
                  return 'Unknown';
                })()}
              </Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Payment Amount</Text>
              <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.paymentAmount || 0)}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Paid On</Text>
              <Text color={textColor} fontWeight="500">{formatDate(invoice?.paidOn) || '-'}</Text>
            </Box>
          </Flex>
          
          {/* Add Notes Section */}
          {invoice?.notes ? (
            <Box>
              <Text color="gray.500" fontSize="sm" mb={2} display="flex" alignItems="center" gap={2}>
                <Icon as={FiImage} color="purple.400" boxSize={4} />
                Notes
              </Text>
              <Box
                p={4}
                bg="purple.50"
                borderLeft="4px solid"
                borderColor="purple.300"
                borderRadius="md"
                borderWidth="1px"
                maxH="120px"
                overflowY="auto"
              >
                <Text color={textColor} whiteSpace="pre-wrap">
                  {invoice.notes}
                </Text>
              </Box>
            </Box>
          ) : null}
        </Flex>
      )}
    </Card>
  );

  // Update the renderAmountDetailsSection to disable edit button
  const renderAmountDetailsSection = () => (
    <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A" mb={6} position="relative">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="1rem" fontWeight="600" color="#42505C">
          Amount Details
        </Text>
      </Flex>
      {editingSection === 'amount' ? (
        <>
          <Flex wrap="wrap" gap={8}>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Invoice Amount</Text>
              <Input
                value={editedData.invoiceAmount}
                onChange={(e) => handleInputChange('invoiceAmount', e.target.value)}
                placeholder="Invoice Amount"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Advance Amount</Text>
              <Input
                value={editedData.advanceAmount}
                onChange={(e) => handleInputChange('advanceAmount', e.target.value)}
                placeholder="Advance Amount"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Escrow Amount</Text>
              <Input
                value={editedData.escrowAmount}
                onChange={(e) => handleInputChange('escrowAmount', e.target.value)}
                placeholder="Escrow Amount"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Fee Amount</Text>
              <Input
                value={editedData.feeAmount}
                onChange={(e) => handleInputChange('feeAmount', e.target.value)}
                placeholder="Fee Amount"
              />
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Payment Amount</Text>
              <Input
                value={editedData.paymentAmount}
                onChange={(e) => handleInputChange('paymentAmount', e.target.value)}
                placeholder="Payment Amount"
              />
            </Box>
          </Flex>
          <Flex justify="flex-end" mt={4} gap={2}>
            {renderSaveButton('amount')}
            <Button variant="ghost" onClick={handleClose} isDisabled={loadingSave}>Close</Button>
          </Flex>
        </>
      ) : (
        <Flex wrap="wrap" gap={8}>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Invoice Amount</Text>
            <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.amount || 0)}</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Funded Amount</Text>
            <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.fundedAmount || 0)}</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Collected Amount</Text>
            <Text color={textColor} fontWeight="500">{formatCurrency((invoice?.amount - invoice?.remainingAmount) || 0)}</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Payment Amount</Text>
            <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.paymentAmount || 0)}</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Escrow Amount</Text>
            <Text color={textColor} fontWeight="500">{formatCurrency(invoice?.escrowAmount || 0)}</Text>
          </Box>
        </Flex>
      )}
    </Card>
  );

  // Update the renderTermsSection to disable edit button
  const renderTermsSection = () => (
    <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A" position="relative">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="1rem" fontWeight="600" color="#42505C">
          Terms
        </Text>
        {editingSection !== 'terms' && !isActionsDisabled() && (
          <IconButton
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            aria-label="Edit Terms"
            onClick={() => handleEditClick('terms')}
          />
        )}
      </Flex>
      {editingSection === 'terms' ? (
        <>
          <Flex wrap="wrap" gap={8}>
            <Box minW="180px" flex={1}>
              <Text color="gray.500" fontSize="sm">Select Terms</Text>
              <Select
                value={editedData.termId || ''}
                onChange={(e) => {
                  const selectedTerm = availableTerms.find(term => term._id === e.target.value);
                  if (selectedTerm) {
                    setEditedData({
                      termId: selectedTerm._id,
                      escrowReserveRate: selectedTerm.escrowReserveRate,
                      purchaseRate: selectedTerm.purchaseRate,
                      advanceRate: selectedTerm.advanceRate,
                      feePerInvoice: selectedTerm.feePerInvoice,
                      minimumPurchaseFee: selectedTerm.minimumPurchaseFee,
                    });
                  }
                }}
                placeholder="Select terms"
              >
                {availableTerms.map(term => (
                  <option key={term._id} value={term._id}>
                    {term.title}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>
          <Flex wrap="wrap" gap={8} mt={4}>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Escrow Reserve Rate</Text>
              <Text color={textColor} fontWeight="500">{editedData.escrowReserveRate}%</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Purchase Rate</Text>
              <Text color={textColor} fontWeight="500">{editedData.purchaseRate}%</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Advance Rate</Text>
              <Text color={textColor} fontWeight="500">{editedData.advanceRate}%</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Fee Per Invoice</Text>
              <Text color={textColor} fontWeight="500">${editedData.feePerInvoice}</Text>
            </Box>
            <Box minW="180px">
              <Text color="gray.500" fontSize="sm">Minimum Purchase Fee</Text>
              <Text color={textColor} fontWeight="500">${editedData.minimumPurchaseFee}</Text>
            </Box>
          </Flex>
          <Flex justify="flex-end" mt={4} gap={2}>
            {renderSaveButton('terms')}
            <Button variant="ghost" onClick={handleClose} isDisabled={loadingSave}>Close</Button>
          </Flex>
        </>
      ) : (
        <Flex wrap="wrap" gap={8}>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Escrow Reserve Rate</Text>
            <Text color={textColor} fontWeight="500">{selectedInvoice?.termId?.escrowReserveRate || invoice?.escrowReserveRate}%</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Purchase Rate</Text>
            <Text color={textColor} fontWeight="500">{ selectedInvoice?.termId?.purchaseRate || invoice?.purchaseRate}%</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Advance Rate</Text>
            <Text color={textColor} fontWeight="500">{selectedInvoice?.termId?.advanceRate || invoice?.advanceRate}%</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Fee Per Invoice</Text>
            <Text color={textColor} fontWeight="500">${selectedInvoice?.termId?.feePerInvoice || invoice?.feePerInvoice}</Text>
          </Box>
          <Box minW="180px">
            <Text color="gray.500" fontSize="sm">Minimum Purchase Fee</Text>
            <Text color={textColor} fontWeight="500">${selectedInvoice?.termId?.minimumPurchaseFee || invoice?.minimumPurchaseFee}</Text>
          </Box>
        </Flex>
      )}
    </Card>
  );

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setIsViewerOpen(true);
  };

  // Add this check for rejections
  const hasRejections = invoice?.rejections && invoice.rejections.length > 0;

  return (
    <>
      <Box p="0px">
        <Card p="0px">
          <Flex direction="column" gap={6}>
            {/* Header */}
            <Flex align="center" mb={4}>
              <Icon
                as={FiArrowLeft}
                w={6}
                h={6}
                cursor="pointer"
                onClick={onBack}
                mr={4}
              />
              <Box flex="1">
                <Text fontSize="2xl" fontWeight="bold">Invoice Details</Text>
                <HStack spacing={2}>
                  <Text fontSize="md" color="gray.500">#{invoice?.invoiceNumber}</Text>
                  <Badge 
                    colorScheme={getStatusColor(invoice?.status)}
                    bg={getStatusBgColor(invoice?.status)}
                  >
                    {getStatusDisplayName(invoice?.status)}
                  </Badge>
                </HStack>
              </Box>
              {!isActionsDisabled() && (
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    bg={menuBg}
                    color="gray.600"
                    fontWeight="500"
                    size="md"
                    px={6}
                    _hover={{ bg: menuHoverBg }}
                    _active={{ bg: menuHoverBg }}
                  >
                    Status
                  </MenuButton>
                  <MenuList>
                    {statusOptions.map((status) => {
                      const remainingAmount = parseFloat(invoice?.remainingAmount) || 0;
                      const isDisabled = (status === "Closed" && remainingAmount > 0) || 
                                        (status === "Funded" && !invoice?.termId);
                      
                      return (
                        <MenuItem
                          key={status}
                          onClick={() => handleStatusChange(status)}
                          isDisabled={isDisabled}
                          title={isDisabled ? 
                            (status === "Closed" ? 
                              "Cannot close invoice with remaining balance" : 
                              "Cannot fund invoice without terms") 
                            : ""}
                        >
                          {status}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
            </Flex>

            {/* Summary Cards */}
            <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
              <Flex gap={4} mb={4}>
                <Card flex="1" p="24px">
                  <Text color="gray.500" fontSize="sm" mb={2}>Invoice Amount</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatCurrency(invoice?.amount || 0)}</Text>
                </Card>
                <Card flex="1" p="24px">
                  <Text color="gray.500" fontSize="sm" mb={2}>Funded Amount</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatCurrency(invoice?.fundedAmount || 0)}</Text>
                </Card>
                <Card flex="1" p="24px">
                  <Text color="gray.500" fontSize="sm" mb={2}>Collected Amount</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatCurrency((invoice?.amount - invoice?.remainingAmount) || 0)}</Text>
                </Card>
                <Card flex="1" p="24px">
                  <Text color="gray.500" fontSize="sm" mb={2}>Payment Amount</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatCurrency(invoice?.paymentAmount || 0)}</Text>
                </Card>
                <Card flex="1" p="24px">
                  <Text color="gray.500" fontSize="sm" mb={2}>Expected Due Date</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatDate(invoice?.expectedDueDate)}</Text>
                </Card>
              </Flex>
            </Card>

            {/* Tabs */}
            <Tabs variant="soft-rounded" colorScheme="purple">
              <TabList mb="6">
                <Tab
                  _focus={{ boxShadow: "none", outline: "none" }}
                  onClick={e => e.target.blur()}
                >
                  Details
                </Tab>
                <Tab
                  _focus={{ boxShadow: "none", outline: "none" }}
                  onClick={e => e.target.blur()}
                >
                  Documents
                </Tab>
                <Tab
                  _focus={{ boxShadow: "none", outline: "none" }}
                  onClick={e => e.target.blur()}
                >
                  Activity Log
                </Tab>
                {hasRejections && (
                  <Tab
                    _focus={{ boxShadow: "none", outline: "none" }}
                    onClick={e => e.target.blur()}
                  >
                    <HStack spacing={2}>
                      <Text>Rejections</Text>
                      <Badge colorScheme="red" borderRadius="full" px={2}>
                        {invoice.rejections.length}
                      </Badge>
                    </HStack>
                  </Tab>
                )}
              </TabList>
              <TabPanels>
                <TabPanel px="0">
                  {renderInformationSection()}
                  {renderAmountDetailsSection()}
                  {renderTermsSection()}
                </TabPanel>
                <TabPanel px="0">
                  <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
                    <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={6}>
                      Documents
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {invoice?.documents?.map((doc) => (
                        <Flex
                          key={doc._id}
                          p={4}
                          borderWidth="1px"
                          borderColor={borderColor}
                          borderRadius="md"
                          align="center"
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => handleDocumentClick(doc)}
                        >
                          {doc.fileType === 'pdf' ? (
                            <Box
                              w="40px"
                              h="40px"
                              mr={4}
                              flexShrink={0}
                              display="flex"
                              alignItems="center"
                            >
                              <img src={PdfIcon} alt="PDF Document" style={{ width: '34px' }} />
                            </Box>
                          ) : (
                            <Box
                              w="40px"
                              h="40px"
                              borderRadius="md"
                              overflow="hidden"
                              mr={4}
                              flexShrink={0}
                            >
                              <img
                                src={doc.url}
                                alt={getFileName(doc.url)}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          )}
                          <Box flex="1">
                            <Text fontWeight="500" color={textColor}>
                              {getFileName(doc.url)}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Uploaded {formatDate(doc.createdAt)}
                            </Text>
                          </Box>
                          <HStack spacing={4}>
                            <Text color="gray.500" fontSize="sm">
                              {doc.fileType?.toUpperCase() || 'UNKNOWN'}
                            </Text>
                            <IconButton
                              icon={<FiDownload />}
                              variant="ghost"
                              size="sm"
                              aria-label="Download"
                              onClick={() => doc.url && window.open(doc.url, '_blank')}
                              isDisabled={!doc.url}
                            />
                          </HStack>
                        </Flex>
                      ))}
                      {(!invoice?.documents || invoice.documents.length === 0) && (
                        <Text color="gray.500" textAlign="center">
                          No documents available
                        </Text>
                      )}
                    </VStack>
                  </Card>
                </TabPanel>
                <TabPanel px="0">
                  <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
                    <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={4}>
                      Activity Log
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {invoice?.activities?.map((activity, index, array) => (
                        <Flex key={index} gap={4}>
                          <Box w="24px" position="relative">
                            {index === 0 ? (
                              <Box
                                w="24px"
                                h="24px"
                                borderRadius="full"
                                bg="green.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Box 
                                  w="8px" 
                                  h="8px" 
                                  borderRadius="full" 
                                  bg="green.500" 
                                />
                              </Box>
                            ) : (
                              <Box
                                w="24px"
                                h="24px"
                                borderRadius="full"
                                bg="green.500"
                                border="2px solid"
                                borderColor="green.500"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <CheckIcon w={3} h={3} color="white" />
                              </Box>
                            )}
                            {index !== array.length - 1 && (
                              <Box
                                position="absolute"
                                left="11px"
                                top="24px"
                                width="2px"
                                height="calc(100% + 16px)"
                                bg="gray.200"
                              />
                            )}
                          </Box>
                          <Box flex="1">
                            <Text color={textColor} fontWeight="500">
                              {activity.description}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                              {formatDate(activity.createdAt)}
                            </Text>
                            {activity.reason && (
                              <Text color="gray.500" fontSize="sm" mt={1}>
                                Reason: {activity.reason}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ))}
                      {(!invoice?.activities || invoice.activities.length === 0) && (
                        <Text color="gray.500" textAlign="center">
                          No activities recorded yet
                        </Text>
                      )}
                    </VStack>
                  </Card>
                </TabPanel>
                {hasRejections && (
                  <TabPanel px="0">
                    <RejectionReasonsTab rejections={invoice.rejections} />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Flex>
        </Card>
      </Box>
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusUpdateConfirm}
        status={selectedStatus}
        loading={loadingStatus}
      />
      <DocumentViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        document={selectedDocument}
      />
    </>
  );
};

export default InvoiceDetails;
